import React, { useEffect, useRef, useState, useCallback } from "react";

// Same gradient chatModal.jsx uses — kept in sync here too so the send
// button and own-message bubbles match branding across desktop and mobile.
const BRAND_GRADIENT =
  "linear-gradient(135deg, var(--color-primary), var(--color-primary-2))";

// presetUser: mirrors chatModal.jsx — jump straight into a conversation with
// a specific { user_id, name, email, user_type } instead of the chat list.
function ChatMobile({ isOpen, closeModal, presetUser }) {
  const [availableUsers, setAvailableUsers] = useState({});
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [userToken, setUserToken] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [isTyping, setIsTyping] = useState(false);
  const [showChat, setShowChat] = useState(false);
  // Per-contact unread counts, keyed by user_id — badge next to each name.
  const [unreadByUser, setUnreadByUser] = useState({});

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Same fix as chatModal.jsx: handleWebSocketMessage reads this instead of
  // the selectedUser state directly, so picking a different chat doesn't
  // change its identity and cascade into tearing down/rebuilding the whole
  // WebSocket connection.
  const selectedUserRef = useRef(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachMenuRef = useRef(null);
  const galleryInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_URL;
  const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;
  const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_URL;

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes cmMobileSpin { to { transform: rotate(360deg); } }
      @keyframes cmMobileAttachIn {
        from { opacity: 0; transform: translateY(6px) scale(.97); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // 🧠 Decode Token
  useEffect(() => {
    const token = localStorage.getItem("Token") || "";
    setUserToken(token);
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setCurrentUser({
          id: payload.user_id,
          username: payload.username,
          email: payload.email,
          user_type: payload.user_type,
        });
      } catch {}
    }
  }, []);

  // 🧠 Auto-scroll when messages change
  useEffect(() => {
    const wrapper = document.querySelector("#messages-wrapper");
    if (wrapper) wrapper.scrollTop = wrapper.scrollHeight;
  }, [messages]);

  // 🧠 Default Admin Contacts
  const getDefaultAdminContacts = useCallback(
    () => ({
      admins: [
        {
          user_id: 1,
          name: "Admin Support",
          email: "admin@packgenie.com",
          user_type: "admin",
        },
      ],
    }),
    []
  );

  // 🧠 Handle WebSocket Messages
  const handleWebSocketMessage = useCallback(
    (data) => {
      switch (data.action) {
        case "auth_success":
          setIsConnected(true);
          break;
        case "messages_fetched":
          if (data.otherUserId === selectedUserRef.current?.user_id) {
            setMessages(data.messages || []);
          }
          break;
        case "message_received":
          if (
            selectedUserRef.current &&
            (data.senderId === selectedUserRef.current.user_id ||
              data.receiverId === selectedUserRef.current.user_id)
          ) {
            const newMsg = {
              message_id: data.messageId,
              sender_id: data.senderId,
              receiver_id: data.receiverId,
              message: data.message,
              attachment_url: data.attachmentUrl || null,
              attachment_type: data.attachmentType || null,
              attachment_name: data.attachmentName || null,
              created_at: data.timestamp,
              is_read: data.isRead || false,
            };
            setMessages((prev) =>
              prev.some((m) => m.message_id === data.messageId)
                ? prev
                : [...prev, newMsg]
            );
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(
                JSON.stringify({
                  action: "mark_as_read",
                  messageId: data.messageId,
                  senderId: data.senderId,
                })
              );
            }
          } else {
            setUnreadByUser((prev) => ({
              ...prev,
              [data.senderId]: (prev[data.senderId] || 0) + 1,
            }));
            window.dispatchEvent(new CustomEvent("chat-unread-changed"));
          }
          break;
        case "message_read":
          setMessages((prev) =>
            prev.map((m) =>
              m.message_id === data.messageId ? { ...m, is_read: true } : m
            )
          );
          break;
        case "online_users":
          if (Array.isArray(data.users)) {
            setOnlineUsers(new Set(data.users.map((u) => u.user_id)));
          }
          break;
        default:
          break;
      }
    },
    []
  );

  // 🧠 Initialize WebSocket
  const initializeWebSocket = useCallback(() => {
    if (!WS_URL || !userToken) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    wsRef.current = new WebSocket(WS_URL);

    wsRef.current.onopen = () => {
      wsRef.current.send(
        JSON.stringify({ action: "authenticate", token: userToken })
      );
    };

    wsRef.current.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        handleWebSocketMessage(data);
      } catch {}
    };

    wsRef.current.onclose = () => {
      setIsConnected(false);
      wsRef.current = null;
      // Stay connected for the whole session (not just while the panel is
      // open) so the header badge and per-contact counts can update live.
      setTimeout(() => initializeWebSocket(), 3000);
    };
  }, [WS_URL, userToken, handleWebSocketMessage]);

  // 🧠 Fetch Available Users
  const fetchAvailableUsers = useCallback(async () => {
    // Subadmins (sales executives and graphic designers — both carry
    // user_type "subadmin") are staff too and now get a correctly-scoped
    // real contact list from /chat/available-users, same as admin.
    if (!["admin", "subadmin"].includes(currentUser?.user_type)) {
      setAvailableUsers(getDefaultAdminContacts());
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/chat/available-users`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      const data = await res.json();

      if (data?.users && typeof data.users === "object") {
        setAvailableUsers(data.users);
      } else if (Array.isArray(data)) {
        setAvailableUsers({ all: data });
      } else if (data?.data && Array.isArray(data.data)) {
        setAvailableUsers({ all: data.data });
      } else {
        setAvailableUsers(getDefaultAdminContacts());
      }
    } catch (err) {
      console.error("Fetch users failed", err);
      setAvailableUsers(getDefaultAdminContacts());
    }
  }, [API_BASE_URL, userToken, currentUser, getDefaultAdminContacts]);

  // Per-conversation unread counts for the contact list badges — same
  // /chat/conversations endpoint chatModal.jsx uses.
  const fetchConversationUnreads = useCallback(async () => {
    if (!userToken || !API_BASE_URL) return;
    try {
      const res = await fetch(`${API_BASE_URL}/chat/conversations`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data?.conversations) ? data.conversations : [];
      const map = {};
      list.forEach((c) => {
        const count = Number(c.unread_count) || 0;
        if (count > 0) map[c.other_user_id] = count;
      });
      setUnreadByUser(map);

      // Same fix as chatModal.jsx: a conversation partner can have an
      // unread message counted here (and by the header's badge) without
      // appearing in availableUsers at all — getAvailableUsers filters to
      // active accounts that currently pass the chat permission matrix,
      // but an existing message doesn't stop being unread just because the
      // sender's role/status changed since. That left the header showing
      // an unread count the panel had no matching row for. Fold any such
      // partner into a "Recent" bucket so the badge has somewhere to sit.
      setAvailableUsers((prev) => {
        const known = new Set();
        Object.values(prev || {}).forEach((group) => {
          if (Array.isArray(group)) group.forEach((u) => known.add(u.user_id));
        });
        const missing = list
          .filter((c) => Number(c.unread_count) > 0 && !known.has(c.other_user_id))
          .map((c) => ({
            user_id: c.other_user_id,
            name: c.other_user_name || `User ${c.other_user_id}`,
            email: "",
            user_type: c.other_user_type,
            profile_image: c.other_user_image,
          }));
        if (missing.length === 0) return prev;
        return { ...prev, Recent: [...(prev?.Recent || []), ...missing] };
      });
    } catch (err) {
      console.error("Error fetching conversation unread counts:", err);
    }
  }, [userToken, API_BASE_URL]);

  // 🧠 Join room & fetch messages
  const joinRoom = (receiverId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ action: "join_room", receiverId: receiverId })
      );
    }
  };

  const getMessages = (userId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ action: "get_messages", otherUserId: userId })
      );
    }
  };

  // 🧠 Send Message
  const sendMessage = () => {
    if (!inputMessage.trim() || !selectedUser) return;

    const tempMsg = {
      message_id: `tmp-${Date.now()}`,
      sender_id: currentUser?.id,
      receiver_id: selectedUser.user_id,
      message: inputMessage.trim(),
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, tempMsg]);

    wsRef.current?.send(
      JSON.stringify({
        action: "send_message",
        receiverId: selectedUser.user_id,
        message: inputMessage.trim(),
        messageType: "text",
      })
    );

    setInputMessage("");
  };

  // Same REST-upload pattern as chatModal.jsx: the backend saves the file,
  // creates the message row, and pushes it to the receiver over their own
  // WS connection itself — we just append the message it hands back here.
  const uploadAttachment = useCallback(
    async (file) => {
      if (!file || !selectedUser || !userToken || !API_BASE_URL) return;
      setShowAttachMenu(false);
      setUploadingAttachment(true);
      try {
        const formData = new FormData();
        formData.append("attachment", file);
        formData.append("receiverId", selectedUser.user_id);

        const res = await fetch(`${API_BASE_URL}/chat/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${userToken}` },
          body: formData,
        });
        const data = await res.json();

        if (!res.ok || !data.success) {
          alert(data?.message || "Failed to send attachment");
          return;
        }

        setMessages((prev) => [...prev, data.data]);
      } catch (err) {
        console.error("Attachment upload failed:", err);
        alert("Failed to send attachment");
      } finally {
        setUploadingAttachment(false);
      }
    },
    [selectedUser, userToken, API_BASE_URL]
  );

  const handleAttachmentFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadAttachment(file);
  };

  useEffect(() => {
    if (!showAttachMenu) return;
    const onClickOutside = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setShowAttachMenu(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [showAttachMenu]);

  // 🧠 Mount
  useEffect(() => {
    if (currentUser) {
      fetchAvailableUsers();
      fetchConversationUnreads();
      initializeWebSocket();
    }
    return () => wsRef.current?.close();
  }, [
    currentUser,
    fetchAvailableUsers,
    fetchConversationUnreads,
    initializeWebSocket,
  ]);

  useEffect(() => {
    if (!isConnected) return;
    const interval = setInterval(fetchConversationUnreads, 10000);
    return () => clearInterval(interval);
  }, [isConnected, fetchConversationUnreads]);

  // 🧠 Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    joinRoom(user.user_id);
    setTimeout(() => getMessages(user.user_id), 300);
    setShowChat(true);
    setUnreadByUser((prev) => {
      if (!prev[user.user_id]) return prev;
      const next = { ...prev };
      delete next[user.user_id];
      return next;
    });
    window.dispatchEvent(new CustomEvent("chat-unread-changed"));
  };

  // 🧠 Jump straight into presetUser's conversation once connected.
  useEffect(() => {
    if (isOpen && presetUser && isConnected) {
      handleUserSelect(presetUser);
    }
  }, [isOpen, presetUser, isConnected]);

  // ---------------------------
  //         RENDER
  // ---------------------------
  if (!isOpen) return null;

  return (
    <div
      style={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) closeModal();
      }}
    >
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button onClick={closeModal} style={styles.closeBtn}>
          ×
        </button>

        {!showChat ? (
          <>
            <div style={styles.header}>Chats</div>
            <div style={styles.userList}>
              {Object.keys(availableUsers).map((key) => {
                const users = availableUsers[key];
                if (!Array.isArray(users) || users.length === 0) return null;
                return (
                  <div key={key}>
                    <div style={styles.groupTitle}>
                      {key.replace("_", " ").toUpperCase()}
                    </div>
                    {users.map((u) => (
                      <div
                        key={u.user_id}
                        onClick={() => handleUserSelect(u)}
                        style={styles.userItem}
                      >
                        <div style={styles.avatar}>
                          {u.name?.charAt(0)?.toUpperCase() || "U"}
                          {onlineUsers.has(u.user_id) && (
                            <div style={styles.onlineDot} />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.name}>{u.name}</div>
                          <div style={styles.email}>{u.email}</div>
                        </div>
                        {unreadByUser[u.user_id] > 0 && (
                          <span style={styles.userUnreadBadge}>
                            {unreadByUser[u.user_id] > 99
                              ? "99+"
                              : unreadByUser[u.user_id]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div style={styles.chatView}>
            <div style={styles.chatHeader}>
              <button onClick={() => setShowChat(false)} style={styles.backBtn}>
                ←
              </button>
              <div style={styles.chatTitle}>
                {selectedUser?.name}
                {onlineUsers.has(selectedUser?.user_id) ? (
                  <span style={styles.onlineText}> • Online</span>
                ) : (
                  <span style={styles.offlineText}> • Offline</span>
                )}
              </div>
            </div>

            {/* Scrollable messages */}
            <div style={styles.messagesWrapper} id="messages-wrapper">
              {messages.map((msg, i) => {
                const isOwn = msg.sender_id === currentUser?.id;
                return (
                  <div
                    key={msg.message_id ?? i}
                    style={{
                      display: "flex",
                      justifyContent: isOwn ? "flex-end" : "flex-start",
                      marginBottom: "6px",
                    }}
                  >
                    <div
                      style={{
                        ...styles.messageBubble,
                        background: isOwn
                          ? BRAND_GRADIENT
                          : "var(--color-surface)",
                        border: isOwn
                          ? "none"
                          : "1px solid var(--color-border)",
                        borderBottomRightRadius: isOwn ? "6px" : "18px",
                        borderBottomLeftRadius: isOwn ? "18px" : "6px",
                      }}
                    >
                      {msg.attachment_url &&
                        (msg.attachment_type === "image" ? (
                          <a
                            href={`${IMAGE_BASE_URL}${msg.attachment_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <img
                              src={`${IMAGE_BASE_URL}${msg.attachment_url}`}
                              alt={msg.attachment_name || "attachment"}
                              style={styles.attachmentImage}
                            />
                          </a>
                        ) : (
                          <a
                            href={`${IMAGE_BASE_URL}${msg.attachment_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              ...styles.attachmentDoc,
                              ...(isOwn
                                ? styles.attachmentDocOwn
                                : styles.attachmentDocOther),
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <span style={styles.attachmentDocName}>
                              {msg.attachment_name || "Document"}
                            </span>
                          </a>
                        ))}
                      {msg.message && (
                        <div
                          style={{
                            ...styles.messageText,
                            color: isOwn ? "#fff" : "var(--color-text-primary)",
                          }}
                        >
                          {msg.message}
                        </div>
                      )}
                      <div
                        style={{
                          ...styles.messageTime,
                          color: isOwn
                            ? "rgba(255,255,255,.8)"
                            : "var(--color-text-tertiary)",
                        }}
                      >
                        {new Date(msg.created_at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {isOwn && (
                          <span
                            style={{
                              marginLeft: 4,
                              color: msg.is_read
                                ? "#53BDEB"
                                : "rgba(255,255,255,.8)",
                            }}
                          >
                            {msg.is_read ? "✓✓" : "✓"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Bar */}
            <div style={styles.inputBar}>
              <div style={{ position: "relative" }} ref={attachMenuRef}>
                <button
                  type="button"
                  aria-label="Attach"
                  onClick={() => setShowAttachMenu((v) => !v)}
                  disabled={uploadingAttachment}
                  style={styles.attachBtn}
                >
                  {uploadingAttachment ? (
                    <span style={styles.attachSpinner} />
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05 12.25 20.24a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95L9.42 17.41a1.5 1.5 0 0 1-2.12-2.12l8.49-8.49" />
                    </svg>
                  )}
                </button>
                {showAttachMenu && (
                  <div style={styles.attachMenu}>
                    <button
                      type="button"
                      style={styles.attachMenuItem}
                      onClick={() => galleryInputRef.current?.click()}
                    >
                      <span style={{ ...styles.attachMenuIcon, background: "#8B5CF6" }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="m21 15-5-5L5 21" />
                        </svg>
                      </span>
                      Photos
                    </button>
                    <button
                      type="button"
                      style={styles.attachMenuItem}
                      onClick={() => documentInputRef.current?.click()}
                    >
                      <span style={{ ...styles.attachMenuIcon, background: "#6366F1" }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </span>
                      Document
                    </button>
                    <button
                      type="button"
                      style={styles.attachMenuItem}
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <span style={{ ...styles.attachMenuIcon, background: "#F59E0B" }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                          <circle cx="12" cy="13" r="4" />
                        </svg>
                      </span>
                      Camera
                    </button>
                  </div>
                )}
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*,video/*"
                  style={{ display: "none" }}
                  onChange={handleAttachmentFileChange}
                />
                <input
                  ref={documentInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  style={{ display: "none" }}
                  onChange={handleAttachmentFileChange}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={handleAttachmentFileChange}
                />
              </div>
              <input
                type="text"
                placeholder="Type message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                style={styles.input}
              />
              <button
                onClick={sendMessage}
                style={{
                  ...styles.sendBtn,
                  ...(inputMessage.trim() === "" ? styles.sendBtnDisabled : {}),
                }}
                disabled={!inputMessage.trim()}
              >
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------
//         STYLES
// ---------------------------
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  modal: {
    width: "100%",
    maxWidth: "380px",
    height: "90vh",
    backgroundColor: "var(--color-surface)",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 10px 30px rgba(16,24,40,.14), 0 30px 70px rgba(16,24,40,.18)",
  },
  closeBtn: {
    position: "absolute",
    top: "10px",
    right: "12px",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: "var(--color-surface-2)",
    border: "none",
    fontSize: "18px",
    lineHeight: "26px",
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    zIndex: 1,
  },
  // Existing styles
  container: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "var(--color-surface)",
    position: "relative",
  },
  header: {
    textAlign: "center",
    padding: "15px",
    fontWeight: "700",
    fontSize: "16px",
    color: "var(--color-text-primary)",
    borderBottom: "1px solid var(--color-border)",
  },
  userList: { flex: 1, overflowY: "auto" },
  userUnreadBadge: {
    flex: "0 0 auto",
    marginLeft: "8px",
    minWidth: "20px",
    height: "20px",
    padding: "0 6px",
    borderRadius: "10px",
    background: "var(--color-primary)",
    color: "#fff",
    fontSize: "11px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  groupTitle: {
    fontSize: "10.5px",
    fontWeight: "800",
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: "var(--color-text-tertiary)",
    padding: "12px 15px 7px",
  },
  userItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 15px",
    borderBottom: "1px solid var(--color-border-soft)",
    cursor: "pointer",
    transition: "background .12s ease",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "var(--color-primary)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: 14,
    marginRight: 12,
    position: "relative",
    flexShrink: 0,
  },
  onlineDot: {
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 10,
    height: 10,
    backgroundColor: "var(--color-success-text)",
    border: "2px solid var(--color-surface)",
    borderRadius: "50%",
  },
  name: { fontWeight: 600, fontSize: 14, color: "var(--color-text-primary)" },
  email: { fontSize: 12, color: "var(--color-text-secondary)" },
  chatView: { flex: 1, display: "flex", flexDirection: "column" },
  chatHeader: {
    padding: "12px 16px",
    borderBottom: "1px solid var(--color-border)",
    backgroundColor: "var(--color-surface)",
    display: "flex",
    alignItems: "center",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "var(--color-primary)",
    fontSize: 20,
    marginRight: 10,
    cursor: "pointer",
  },
  chatTitle: { fontSize: 15, fontWeight: "700", color: "var(--color-text-primary)" },
  onlineText: { color: "var(--color-success-text)", fontSize: 12, fontWeight: 600 },
  offlineText: { color: "var(--color-text-tertiary)", fontSize: 12 },
  messagesWrapper: {
    flex: 1,
    overflowY: "scroll",
    padding: "12px 10px 70px",
    backgroundColor: "var(--color-surface-2)",
    maxHeight: "514px",
  },
  messageBubble: {
    maxWidth: "75%",
    padding: "10px 14px",
    borderRadius: "18px",
    wordBreak: "break-word",
    boxShadow: "0 1px 2px rgba(16,24,40,.06)",
  },
  messageText: {
    fontSize: "14px",
    lineHeight: "1.4",
    color: "var(--color-text-primary)",
  },
  messageTime: {
    fontSize: "11px",
    color: "var(--color-text-tertiary)",
    textAlign: "right",
    marginTop: "4px",
  },
  attachBtn: {
    width: "36px",
    height: "36px",
    flex: "0 0 36px",
    borderRadius: "50%",
    border: "1px solid var(--color-border)",
    background: "var(--color-surface-2)",
    color: "var(--color-text-secondary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    marginRight: "8px",
  },
  attachSpinner: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: "2px solid var(--color-border)",
    borderTopColor: "var(--color-primary)",
    animation: "cmMobileSpin .7s linear infinite",
  },
  attachMenu: {
    position: "absolute",
    bottom: "44px",
    left: 0,
    width: "170px",
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "14px",
    boxShadow: "0 10px 30px rgba(16,24,40,.16)",
    padding: "6px",
    zIndex: 1000,
    animation: "cmMobileAttachIn .16s ease both",
  },
  attachMenuItem: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "9px",
    padding: "8px 9px",
    borderRadius: "9px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "12.5px",
    fontWeight: 600,
    color: "var(--color-text-primary)",
    fontFamily: "inherit",
    textAlign: "left",
  },
  attachMenuIcon: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 28px",
  },
  attachmentImage: {
    display: "block",
    maxWidth: "200px",
    maxHeight: "200px",
    borderRadius: "10px",
    objectFit: "cover",
    marginBottom: "4px",
  },
  attachmentDoc: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 10px",
    borderRadius: "10px",
    color: "inherit",
    textDecoration: "none",
    maxWidth: "200px",
    marginBottom: "4px",
  },
  attachmentDocOwn: {
    background: "rgba(255,255,255,.16)",
    border: "1px solid rgba(255,255,255,.22)",
  },
  attachmentDocOther: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
  },
  attachmentDocName: {
    fontSize: "12px",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  inputBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100%",
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    backgroundColor: "var(--color-surface)",
    borderTop: "1px solid var(--color-border)",
    zIndex: 999,
  },
  input: {
    flex: 1,
    padding: "10px 14px",
    borderRadius: "20px",
    border: "1px solid var(--color-border)",
    background: "var(--color-surface-2)",
    color: "var(--color-text-primary)",
    outline: "none",
    fontSize: "14px",
  },
  sendBtn: {
    marginLeft: "8px",
    background: BRAND_GRADIENT,
    color: "white",
    border: "none",
    borderRadius: "20px",
    padding: "10px 18px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(79,70,229,.28)",
  },
  sendBtnDisabled: {
    background: "var(--color-border)",
    color: "var(--color-text-tertiary)",
    boxShadow: "none",
    cursor: "not-allowed",
  },
};

export default ChatMobile;
